/* eslint-disable prettier/prettier */
import { actions as ACTION, mutations as MUTATION} from './types';
import { ProductAssignmentService } from '@/services'

const uniqueArrayOfObjects = (array: any[]) => {
  const uniqueIds = new Set();
  return array.filter((item: { id: number }) => {
    const isNotDuplicate = !uniqueIds.has(item.id);
    uniqueIds.add(item.id);
    return isNotDuplicate;
  })
}

const notFoundInList = (item: { id: string; }, list: any) => {
  return !list.find((listItem: { id: string }) => listItem.id === item.id);
}

const saveList = (params: { data: any, list: any }) => {
  const { data, list } = params;
  const cachedList = [] as any[];

  for (const item of data) {
    const isNotInStore = notFoundInList(item, list);
    if (isNotInStore) cachedList.push(item);
  }
  list.push(...cachedList);
};

const saveProducts = (params: { 
  filterId: number; 
  responseProducts: any[]; 
  endCursor: string; 
  producstByFilter: any;
  fetched: boolean;
}) => {
  const cachedList = [] as any[];
  const {filterId, responseProducts, endCursor, producstByFilter, fetched} = params;
  const productObj = producstByFilter.find((item: { filterId: number }) => item.filterId === filterId);

  if (productObj) {
    for (const product of responseProducts) {
      const isNotInStore = notFoundInList(product, productObj.list);
      if (isNotInStore) cachedList.push(product);
    }
    productObj.endCursor = endCursor;
    productObj.fetched = fetched;
    productObj.list.push(...cachedList);
    return;
  };

  const item = {
    filterId: filterId,
    endCursor: endCursor,
    list: responseProducts,
    fetched: fetched
  }

  producstByFilter.push(item);
};

const actions = {
  [ACTION.GET_PRODUCTS](
    { state, commit }: any, 
    params: {query: string; page_id: number, refresh: boolean, changeQuery: boolean }
  ) {
    return new Promise( async (resolve, reject) => {
      try {
        const { list, queryList, endCursor, endCursorQuery } = state.product.all;
        const { query, page_id, refresh, changeQuery } = params;
        const isQuery = query.length !== 0;

        const res = await ProductAssignmentService.get.all({
          s: query,
          pageId: page_id,
          refresh: refresh,
          nextCursor: isQuery ? endCursorQuery : endCursor,
          assignedOnly: isQuery ? (changeQuery ? true : state.assignedOnlyQuery) : state.assignedOnly,
        });

        const { products, pageInfo } = res;

        state[ isQuery ? 'assignedOnlyQuery' : 'assignedOnly' ] = pageInfo?.hasNextAssignmentOnly;

        const uniqueListProducts = uniqueArrayOfObjects(products);
        const isFetchedAll = !pageInfo?.hasNextPage

        if (isQuery) {
          if (isFetchedAll) commit(MUTATION.SET_FETCHED_ALL_QUERY, "all");
          state.product.all.endCursorQuery = pageInfo?.hasNextPage && pageInfo?.endCursor || '';
          changeQuery 
            ? state.product.all.queryList = uniqueListProducts
            : saveList({ data: uniqueListProducts, list: queryList });
        } else {
          if (isFetchedAll) commit(MUTATION.SET_FETCHED_ALL, "all");
          state.product.all.endCursor = pageInfo?.endCursor;
          saveList({ data: uniqueListProducts, list: list });
        }


        resolve(res.data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        reject(e);
      }
    });
  },
  [ACTION.GET_LIST_BY_FILTER](
    { state, commit }: any,
    params: {query: string; page_id: number, filter: string, refresh: boolean, changeQuery: boolean},
  ) {
    return new Promise(async (resolve, reject) => {
      try {
        const {query, page_id, filter, refresh, changeQuery} = params;
        const { list, queryList, endCursorQuery, endCursor } = state.product[filter];
        const isQuery = query.length !== 0;

        const res = await ProductAssignmentService.get[filter].list({ 
          s: query, 
          pageId: page_id,
          refresh: refresh,
          nextCursor: isQuery ? endCursorQuery : endCursor
        });

        const { pageInfo } = res;
        const data = filter === 'pages' ? res.productPages : res.collections;

        const isFetchedAll =  !data || data.length == 0 || data.length < 20 || !pageInfo?.hasNextPage;

        if (isQuery) {
          if (isFetchedAll) commit(MUTATION.SET_FETCHED_ALL_QUERY, filter);
          state.product[filter].endCursorQuery = pageInfo?.endCursor;
          changeQuery
            ? state.product[filter].queryList = data
            : saveList({data: data, list: queryList});
        } else {
          if (isFetchedAll) commit(MUTATION.SET_FETCHED_ALL, filter);
          state.product[filter].endCursor = pageInfo?.endCursor;
          saveList({data: data, list: list});
        }


        resolve(res.data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        reject(e);
      }
    });
  },
  [ACTION.GET_PRODUCTS_BY_FILTER]: async ({ state }: any, params: { id: any; filter: any; }) => {
    try {
      const {id, filter} = params;
      const producstByFilter = state.product[filter].products;
      const expandedList = producstByFilter.find((item: { filterId: number }) => item.filterId === id);

      const res = await ProductAssignmentService.get[filter].products({
        [filter === 'pages' ? 'pageId' : 'collectionId']: id,
        nextCursor: expandedList?.endCursor || ''
      });

      const isFetchedAll =  
        ! res?.products 
        || (res?.products.length == 0 && !res?.pageInfo?.hasNextPage)
        || !res?.pageInfo?.hasNextPage;

      const payload = {
        responseProducts: uniqueArrayOfObjects(res?.products),
        filterId: id,
        producstByFilter: producstByFilter,
        fetched: isFetchedAll,
        endCursor: res?.pageInfo?.endCursor,
      }

      saveProducts(payload);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  },
  [ACTION.UPDATE_PAGE_ASSIGNMENT]({ state }: any, pageId: any) {
    return new Promise(async (resolve, reject) => {
      try {
        const payload: any = {
          assignedIds: [...state.product.assignment.selected],
          unassignedIds: [...state.product.assignment.unselected],
        };
        if (window._webSocket && window._webSocket?.connection.readyState === 1) {
          payload.isUseSocket = true;
        }
        const res = await ProductAssignmentService.updatePageAssignment(pageId, payload);
        resolve(res);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        reject(e);
      }
    });
  },
};
export default actions;
